"use client"

import { useEffect, useState } from "react"
import { useSocket } from "@/providers/socket-provider"
import { useAppSelector } from "@/redux/hooks"
import { roles } from "@/utils"
import { debounce } from "lodash"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { LinkButton } from "@/components/link-button"
import { Modal } from "@/components/modals/modal"

interface OrderResponse {
  orderId: string
  message: string
  reason: string
  status?: "accepted" | "rejected"
}

interface OrderCancelModalProps {
  allowedRoles: roles[]
  socketEvent: "cancelRequest" | "cancelRequestUpdate"
  title: string
  description: string
  orderLinkBase: string
  showStatus?: boolean
}

function OrderCancelModal({
  allowedRoles,
  socketEvent,
  title,
  description,
  orderLinkBase,
  showStatus = false,
}: OrderCancelModalProps) {
  const auth = useAppSelector((state) => state.auth)
  const token = auth.token
  const user = auth.user
  const role = user?.role

  const isAllowed = allowedRoles.includes(role as roles)

  const { socket, isConnected } = useSocket()
  const [open, setOpen] = useState<boolean>(false)
  const [isMounted, setIsMounted] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [orderData, setOrderData] = useState<OrderResponse | null>(null)

  const debouncedOrderHandler = debounce((data: OrderResponse) => {
    setOrderData(data)
    setOpen(true)
    setIsLoading(false)
  }, 200)

  useEffect(() => {
    setOpen(false)
  }, [])

  useEffect(() => {
    if (!socket || !isConnected || !token || !isAllowed) {
      return
    }

    const orderHandler = (data: OrderResponse) => {
      debouncedOrderHandler(data)
    }

    const errorHandler = (error: { message: string }) => {
      setError(error.message)
      setIsLoading(false)
    }

    socket.on(socketEvent, orderHandler)
    socket.on("error", errorHandler)

    return () => {
      socket.off(socketEvent, orderHandler)
      socket.off("error", errorHandler)
      debouncedOrderHandler.cancel()
    }
  }, [socket, isConnected, token, isAllowed, socketEvent])

  useEffect(() => {
    setIsMounted(true)
  }, [])

  if (!isMounted) {
    return null
  }

  const getStatusBadge = (status?: string) => {
    if (!status) return null

    const statusClasses = {
      accepted: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
    }

    return (
      <span
        className={cn(
          "rounded-md px-2 py-1 text-sm font-medium",
          statusClasses[status as keyof typeof statusClasses]
        )}
      >
        {status.toUpperCase()}
      </span>
    )
  }

  return (
    <Modal
      title={title}
      description={description}
      open={open}
      onClose={() => setOpen(false)}
    >
      {isLoading ? (
        <p>Loading order details...</p>
      ) : error ? (
        <p className="text-destructive">Error: {error}</p>
      ) : orderData ? (
        <div className="space-y-4">
          {showStatus && orderData.status && (
            <div className="flex items-center gap-2">
              <span className="font-bold">Status:</span>
              {getStatusBadge(orderData.status)}
            </div>
          )}
          <p>
            <span className="font-bold">Message:</span>{" "}
            <span className="font-medium">{orderData.message}</span>
          </p>
          {orderData.reason && (
            <p>
              <span className="font-bold">Reason:</span>{" "}
              <span className="font-medium">{orderData.reason}</span>
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Close
            </Button>
            <LinkButton
              href={`${orderLinkBase}/${orderData.orderId}`}
              onClick={() => setOpen(false)}
            >
              View Order
            </LinkButton>
          </div>
        </div>
      ) : (
        <p>No order data available.</p>
      )}
    </Modal>
  )
}

export function OrderCancelModalUser() {
  return (
    <OrderCancelModal
      allowedRoles={[roles.USER]}
      socketEvent="cancelRequestUpdate"
      title="Order Cancel Update"
      description="Your order cancellation request has been processed"
      orderLinkBase="/dashboard/my-orders"
      showStatus={true}
    />
  )
}

export function OrderCancelModalAdmin() {
  return (
    <OrderCancelModal
      allowedRoles={[roles.ADMIN, roles.DEV, roles.SUPER_ADMIN]}
      socketEvent="cancelRequest"
      title="Order Cancel"
      description="A user has requested to cancel an order"
      orderLinkBase="/dashboard/orders-management"
    />
  )
}
